/**
 * BranchInfoSection 컴포넌트
 * 작성자: AI Assistant
 * 설명: 지점 안내(주소, 지도, 운영시간)를 렌더링합니다.
 * props:
 *   - address: 지점 주소 배열
 *   - hours: 운영 시간 정보 배열
 *   - markers: 지도 마커 정보 배열
 */
import { BranchBreadcrumbItem } from '@/data/globals.data';
import { BranchType } from '@/types/branch.type';
import { MarkerType } from '@/types/marker.type';
import Image from 'next/image';
import BranchAddress from './branch-address';

interface SubwayInfo {
  city: string;
  line: string;
  color: string;
  viewName: string;
  detail: string;
}

interface BranchInfoSectionProps {
  address: BranchType['address'];
  roadAddress: string;
  hours: BranchType['hours'];
  markers?: MarkerType[];
  phoneNumber: string;
  subways?: SubwayInfo[];
  operatingHoursNote?: string | null;
}

// 요일 영문 → 한글 변환 맵
// const DAY_MAP: Record<string, string> = {
//   MON: '월',
//   TUE: '화',
//   WED: '수',
//   THU: '목',
//   FRI: '금',
//   SAT: '토',
//   SUN: '일',
//   HOLIDAY: '공휴일',
// };

// 시간 포맷팅: '10:00:00 ~ 17:00:00' → '10:00 ~ 17:00'
function formatHourRange(time: string) {
  if (!time) return '';
  const [start, end] = time.split('~').map((t) => t.trim());
  const format = (t: string) => (t ? t.slice(0, 5) : '');
  return `${format(start)} ~ ${format(end)}`;
}

export default function BranchInfoSection({
  address,
  roadAddress,
  hours,
  markers,
  phoneNumber,
  subways,
  operatingHoursNote,
}: BranchInfoSectionProps) {
  // 오늘 한글 요일 반환
  // function getTodayLabel() {
  //   const jsDay = new Date().getDay();
  //   return ['일', '월', '화', '수', '목', '금', '토'][jsDay];
  // }

  // 평일/주말 시간 가져오기
  function getOperatingHours() {
    if (!hours || hours.length === 0) return null;

    const weekdayHours = hours.find((h) => h.label === 'MON');
    const weekendHours = hours.find((h) => h.label === 'SAT');

    return {
      weekday: weekdayHours ? formatHourRange(weekdayHours.time) : '휴무',
      weekend: weekendHours ? formatHourRange(weekendHours.time) : '휴무',
    };
  }

  const operatingHours = getOperatingHours();

  return (
    <section>
      <h3 className="desktop:pb-8 text-level-2 desktop:typo-24 typo-22 pb-5 font-extrabold">
        {BranchBreadcrumbItem.label}
      </h3>
      <BranchAddress
        address={address}
        roadAddress={roadAddress}
        subways={subways}
        markers={markers || []}
      />
      {/* <div className="h-[280px] w-full rounded-2xl">
        <MapWithMarkers markers={markers || []} />
      </div> */}

      {/* 운영시간 - hours가 있을 때만 표시 */}
      <div className="desktop:flex">
        {hours && hours.length > 0 && (
          <div className="desktop:flex gap-4 pb-9">
            <div className="desktop:w-[100px] desktop:items-start desktop:pb-5 flex pb-4">
              <Image
                src="/images/icons/icon_clock.svg"
                alt="운영시간"
                width={20}
                height={20}
              />
              <span className="typo-20 text-level-3 pl-2 font-bold">
                운영시간
              </span>
            </div>
            <div className="desktop:pl-6 flex flex-col gap-3">
              {(() => {
                // console.log(hours);
                // const todayLabel = getTodayLabel();

                // 요일 순서 정의 (일요일부터 시작)
                // const dayOrder = ['일', '월', '화', '수', '목', '금', '토'];
                // const todayIndex = dayOrder.indexOf(todayLabel);

                // 오늘부터 시작하는 순환 순서 생성
                // const orderedDays = [
                //   ...dayOrder.slice(todayIndex),
                //   ...dayOrder.slice(0, todayIndex),
                // ];

                // 공휴일과 일반 요일 분리
                // const regularDays = hours.filter(
                //   (h) => DAY_MAP[h.label] !== '공휴일',
                // );
                // let holidays = hours.filter((h) => DAY_MAP[h.label] === '공휴일');
                // // 공휴일 중복 제거 (label+time 기준)
                // holidays = holidays.filter(
                //   (h, idx, arr) =>
                //     arr.findIndex(
                //       (x) => x.label === h.label && x.time === h.time,
                //     ) === idx,
                // );

                // 오늘부터 순차적으로 정렬
                // const sortedRegular = orderedDays
                //   .map((day) => regularDays.find((h) => DAY_MAP[h.label] === day))
                //   .filter((item): item is { label: string; time: string } =>
                //     Boolean(item),
                //   );

                return (
                  <>
                    {/* 일반 요일 렌더링 */}
                    {/* {sortedRegular.map(({ label, time }) => {
                    const isToday = DAY_MAP[label] === todayLabel;
                    const displayTime =
                      time && time.trim() ? formatHourRange(time) : '휴무';
                    return (
                      <div
                        key={label + time}
                        className="flex items-center space-x-2 text-lg text-[#333333]"
                      >
                        <span
                          className={`typo-16 font-bold ${
                            isToday ? 'text-main-orange' : 'text-level-4'
                          }`}
                        >
                          {DAY_MAP[label] || label}
                        </span>
                        <span
                          className={`typo-16 font-medium ${
                            isToday ? 'text-main-orange' : 'text-level-4'
                          }`}
                        >
                          {displayTime}
                        </span>
                      </div>
                    );
                  })} */}
                    {/* 공휴일 중복 없이 한 번씩만 렌더링 */}
                    {/* {holidays.map(({ label, time }) => (
                    <div
                      key={label + time}
                      className="flex items-center space-x-2 text-lg text-[#333333]"
                    >
                      <span className="desktop:typo-18 typo-16 text-level-4 font-bold">
                        {DAY_MAP[label] || label}
                      </span>
                      <span className="typo-16 text-level-4 font-medium">
                        {time && time.trim() ? formatHourRange(time) : '휴무'}
                      </span>
                    </div>
                  ))} */}
                    {/* 운영시간 비고(operatingHoursNote)가 있으면 공휴일 아래에 표시 */}
                    <div className="flex items-center space-x-2 text-lg text-[#333333]">
                      <span className="desktop:typo-18 typo-16 text-level-4 font-bold">
                        평일
                      </span>
                      <span className="typo-16 text-level-4 desktop:typo-18 font-medium">
                        {operatingHours?.weekday}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 text-lg text-[#333333]">
                      <span className="desktop:typo-18 typo-16 text-level-4 font-bold">
                        주말
                      </span>
                      <span className="typo-16 text-level-4 desktop:typo-18 font-medium">
                        {operatingHours?.weekend}
                      </span>
                    </div>
                    {operatingHoursNote && (
                      <div className="typo-16 text-[#333333]">
                        <span className="desktop:typo-18 typo-16 text-level-4 font-bold">
                          공휴일
                        </span>{' '}
                        {operatingHoursNote}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* 연락처 - phoneNumber가 있을 때만 표시 */}
      {phoneNumber && (
        <div className="desktop:flex desktop:pb-9 gap-4 pb-7">
          <div className="desktop:w-[100px] desktop:items-start desktop:pb-0 flex pb-4">
            <Image
              src="/images/icons/icon_call.svg"
              alt="연락처"
              width={20}
              height={20}
            />
            <span className="typo-20 text-level-3 pl-2 font-bold">연락처</span>
          </div>
          <div className="desktop:pl-6 flex flex-col gap-3">
            <span className="desktop:typo-18 typo-16 text-level-4 font-bold">
              {phoneNumber}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
